/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Step Controller Hook
 * Single responsibility: Manage step form logic with field enhancements
 */

import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../services/serviceTypes';
import type { STEP_NAMES } from '../constants/steps';
import { updateStepFormData } from '../data-store/copilotSlice';
import { useFormFieldLogic } from '../services/stepServices/formFieldService';
import type { ParsedField } from '../utils/templateParsers';

type StepName = (typeof STEP_NAMES)[keyof typeof STEP_NAMES];

export const useStepController = (
  stepName: StepName,
  toggleState?: string,
  onToggleChange?: (state: string) => void
) => {
  const dispatch = useDispatch();

  const stepFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData[stepName] ?? {}
  );
  const parsedTemplates = useSelector(
    (state: RootState) => state.copilot.parsedTemplates ?? {}
  );
  const revertTimestamp = useSelector(
    (state: RootState) => state.copilot.revertedSteps[stepName] ?? 0
  );

  const stepLogic = useFormFieldLogic({ stepName, stepFormData });

  // Create input handler that persists directly to Redux (restored complex logic)
  const handleInputValueStatus = useMemo(
    () =>
      ({
        attributeName,
        value,
      }: {
        attributeName: string;
        value: string | string[] | { id: string; value: string };
        hasError: boolean;
      }) => {
        let processedValue: unknown;

        if (attributeName === 'endpoints.ownerType') {
          // Store as array for ownerType and update toggle state
          processedValue = Array.isArray(value) ? value : [String(value)];

          // Also update the toggle state for UI persistence
          dispatch(
            updateStepFormData({
              stepName,
              formData: { toggleState: String(value) },
            })
          );
        } else if (attributeName === 'endpoints.ownerkey') {
          // Store as object for ownerkey
          processedValue = typeof value === 'object' ? value : value;
        } else {
          processedValue = String(value);
        }

        // Persist to Redux
        dispatch(
          updateStepFormData({
            stepName,
            formData: { [attributeName]: processedValue },
          })
        );
      },
    [dispatch, stepName]
  );

  const fields: ParsedField[] = useMemo(() => {
    const rawFields: unknown[] = (parsedTemplates[stepName] as unknown[]) ?? [];

    if (!Array.isArray(rawFields)) {
      return [];
    }

    const parsedFields = rawFields as ParsedField[];

    return parsedFields.map((field) => {
      // Get current value from Redux, fallback to template defaultValue
      const formValue = stepFormData[field.attributeName || ''];
      const currentValue =
        formValue !== undefined ? formValue : field.defaultValue || '';

      const baseField = {
        ...field,
        inputValueStatus: handleInputValueStatus,
        payloadAtrributeName: field.payloadAtrributeName ?? field.attributeName,
        defaultValue: currentValue,
        key: `${field.attributeName}-${revertTimestamp}`, // Only changes on revert
      };

      // Add toggle state props for ToggleGroup fields
      if (
        field.type === 'ToggleGroup' &&
        field.attributeName === 'endpoints.ownerType'
      ) {
        // Get the actual ownerType from Redux, handle both string and array formats
        const ownerTypeValue = stepFormData['endpoints.ownerType'];
        const actualOwnerType = Array.isArray(ownerTypeValue)
          ? ownerTypeValue[0]
          : ownerTypeValue;

        const currentToggleState =
          toggleState || (actualOwnerType as string) || 'User';

        const selectionIndex =
          field.options?.findIndex((opt) => opt.value === currentToggleState) ??
          0;

        const calculatedSelection = selectionIndex + 1;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (baseField as any).selection = calculatedSelection; // ToggleGroup uses 1-based indexing

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (baseField as any).setSelection = (index: number) => {
          const selectedOption = field.options?.[index - 1];

          if (selectedOption && onToggleChange) {
            onToggleChange(selectedOption.value);
          }
        };

        (baseField as any).key = `${field.attributeName}-${currentToggleState}`; // Force re-render
      }

      // Add toggle state props for dropdown fields
      if (
        field.type === 'Dropdown' &&
        field.attributeName === 'endpoints.ownerkey'
      ) {
        (baseField as any).toggleGroup = toggleState;
        (baseField as any).onToggleDetected = onToggleChange;

        // Set the value prop for dropdown
        (baseField as any).value = currentValue;
      }

      // Delegate step-specific field enhancement to service
      const enhancedField = stepLogic.enhanceField(field, baseField);

      return enhancedField;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parsedTemplates,
    stepName,
    stepFormData,
    handleInputValueStatus,
    toggleState,
    onToggleChange,
  ]);

  return {
    // Data (restored)
    formData: stepFormData as Record<string, unknown>,
    fields,
    stepLogic,
    handleInputValueStatus,

    // Actions (placeholder for now)
    handleSave: () => ({ isValid: true, data: stepFormData }),
  };
};

export default useStepController;
